// HotX Console 0.1.121 域名续费写入串行化与会话隔离。实际修改时间：2026-09-29 16:00:24 CST；精确父基线：8db2928f2d6be2bf6c8ce743436c67f913eb728d；变更：CONSOLE-HOME-DATA-PASSWORD-20260929。
import { readonly, ref, shallowRef } from 'vue'
import { pointApi, pointErrorMessage, type DomainPlanOverview } from '../api/pointApi'
import { tokenStorage } from '../api/client'

const overview = shallowRef<DomainPlanOverview | null>(null)
const loading = ref(false)
const loadError = ref('')
const updatingDomainIds = ref<Set<number>>(new Set())

const pendingLoads = new Map<string, Promise<DomainPlanOverview>>()
let loadedAt = 0
let overviewSessionKey = ''
let overviewRevision = 0
let mutationQueue: Promise<void> = Promise.resolve()
const updatingDomainCounts = new Map<number, number>()
const sharedLoadWindowMs = 2_000

function currentSessionKey() {
  const customerId = tokenStorage.getCustomerId()
  if (Number.isSafeInteger(customerId) && Number(customerId) > 0) return `customer:${customerId}`
  const accessToken = tokenStorage.getAccessToken()
  // 正常认证会话始终有 customerId；仅对损坏的历史会话以令牌作临时内存键，
  // 避免两个缺失 customerId 的账号共享在途响应。该值不会记录、持久化或输出。
  return accessToken ? `session:${accessToken}` : 'guest'
}

function sessionChangedError() {
  return new Error('登录状态已变化，请刷新后重试。')
}

function markDomainUpdating(domainId: number, updating: boolean) {
  const nextCount = Math.max(0, (updatingDomainCounts.get(domainId) || 0) + (updating ? 1 : -1))
  if (nextCount > 0) updatingDomainCounts.set(domainId, nextCount)
  else updatingDomainCounts.delete(domainId)
  updatingDomainIds.value = new Set(updatingDomainCounts.keys())
}

function concreteError(error: unknown, fallback: string) {
  const detail = error instanceof Error && error.message.trim() ? error.message.trim() : fallback
  return pointErrorMessage(error, detail)
}

async function loadDomainPlans(force = false) {
  const now = Date.now()
  const sessionKey = currentSessionKey()
  if (!force && overview.value && overviewSessionKey === sessionKey && now - loadedAt < sharedLoadWindowMs) {
    return overview.value
  }
  const existingLoad = pendingLoads.get(sessionKey)
  if (existingLoad) {
    return existingLoad
  }

  const revisionAtStart = overviewRevision
  loading.value = true
  loadError.value = ''
  const request = pointApi.domainPlans()
  pendingLoads.set(sessionKey, request)

  try {
    const result = await request
    if (currentSessionKey() !== sessionKey) throw sessionChangedError()
    // 保存动作开始前或执行中的读取可能返回旧整包快照；只接受仍是最新的一次读取。
    if (revisionAtStart === overviewRevision) {
      overview.value = result
      overviewSessionKey = sessionKey
      loadedAt = Date.now()
    }
    return result
  } catch (error) {
    if (currentSessionKey() === sessionKey && revisionAtStart === overviewRevision) {
      loadError.value = concreteError(error, '无法判断自动续费状态，请刷新后重试。')
    }
    throw error
  } finally {
    if (pendingLoads.get(sessionKey) === request) pendingLoads.delete(sessionKey)
    loading.value = pendingLoads.size > 0
  }
}

function setDailyAutoRenew(domainId: number, enabled: boolean) {
  const sessionKey = currentSessionKey()
  markDomainUpdating(domainId, true)
  const operation = mutationQueue.then(async () => {
    if (currentSessionKey() !== sessionKey) throw sessionChangedError()
    // 后端返回完整 overview。串行提交确保后一份快照一定包含前一项已落库结果。
    overviewRevision += 1
    try {
      const result = await pointApi.setDomainDailyAutoRenew(domainId, enabled)
      if (currentSessionKey() !== sessionKey) throw sessionChangedError()
      overviewRevision += 1
      overview.value = result
      overviewSessionKey = sessionKey
      loadedAt = Date.now()
      loadError.value = ''
      return result
    } catch (error) {
      overviewRevision += 1
      throw error
    } finally {
      markDomainUpdating(domainId, false)
    }
  })
  mutationQueue = operation.then(() => undefined, () => undefined)
  return operation
}

export function useDomainDailyRenewState() {
  return {
    overview: readonly(overview),
    loading: readonly(loading),
    loadError: readonly(loadError),
    updatingDomainIds: readonly(updatingDomainIds),
    loadDomainPlans,
    setDailyAutoRenew,
    concreteError
  }
}
